import { FC, ReactElement, useState } from 'react';
import classNames from 'classnames';
import { DialogPanel, DialogTitle } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faChevronLeft,
  faChevronRight,
  faMagnifyingGlass,
  faClock,
} from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useModal } from 'xstate/hooks/useModal';
import { MoveMemoModalPayload } from 'xstate/machines/ModalMachine';
import { closeModal } from 'xstate/actors/ModalActor';
import useBoxes from 'networking/mindfeed/hooks/useBoxes';
import { useMoveMemo } from 'networking/mindfeed/hooks/useMoveMemo';
import { collectionsQuery } from 'networking/mindfeed/queries/collections';
import { Box } from 'models/Box';
import type { Collection } from 'models/Collection';
import type { Memo } from 'models/Memo';
import BoxIcon from 'assets/icons/icon-box.svg';
import CollectionIcon from 'assets/icons/icon-collection.svg';
import { contrastingForeground } from 'utils/ColorUtils';
import { useBoxLabels } from 'hooks/useBoxLabels';
import { useTranslations } from 'next-intl';

// Move a memo to a destination = (box, collection?). Two lightweight steps —
// pick a box, then pick a collection within it (or "Timeline" for no
// collection) — each with a search box so it scales from a handful of
// boxes/collections to many. Selecting the collection performs the move.
const MoveMemoModal = (): ReactElement | null => {
  const t = useTranslations();
  const { boxName } = useBoxLabels();
  const { payload } = useModal() as { payload: MoveMemoModalPayload };
  const memo = payload?.memo;

  const { data: boxes = [] } = useBoxes();
  const [selectedBox, setSelectedBox] = useState<Box | null>(null);
  const [query, setQuery] = useState('');
  const moveMemo = useMoveMemo({ onSuccess: closeModal });

  if (!memo) return null;

  const editableBoxes = boxes.filter((b) => b.permissions.capabilities.canEdit);

  const goToBoxes = () => {
    setSelectedBox(null);
    setQuery('');
  };

  const pickBox = (box: Box) => {
    setSelectedBox(box);
    setQuery('');
  };

  const move = (box: Box, collection?: Collection) => {
    moveMemo.mutate({
      memo,
      destinationBox: box,
      destinationCollection: collection,
    });
  };

  const header = selectedBox
    ? {
        title: boxName(selectedBox.name),
        subtitle: t('moveMemo.chooseCollection'),
        onBack: goToBoxes,
      }
    : {
        title: t('moveMemo.title'),
        subtitle: t('moveMemo.chooseBox'),
        onBack: undefined,
      };

  return (
    <div className={styles.outer}>
      <div className={styles.inner}>
        <DialogPanel transition className={styles.panel}>
          <div className={styles.header.container}>
            <div className={styles.header.titleWrap}>
              {header.onBack && (
                <button
                  type="button"
                  onClick={header.onBack}
                  aria-label={t('common.back')}
                  className={styles.header.back}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
              )}
              <div className="min-w-0">
                <DialogTitle as="h2" className={styles.header.title}>
                  {header.title}
                </DialogTitle>
                <p className={styles.header.subtitle}>{header.subtitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={closeModal}
              aria-label={t('common.close')}
              className={styles.header.close}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>

          <div className={styles.search.wrap}>
            <div className={styles.search.field}>
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className={styles.search.icon}
                aria-hidden="true"
              />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={
                  selectedBox ? t('collections.search') : t('boxes.search')
                }
                className={styles.search.input}
              />
            </div>
          </div>

          <div className={styles.list}>
            {selectedBox ? (
              <CollectionStep
                box={selectedBox}
                memo={memo}
                query={query}
                disabled={moveMemo.isPending}
                onMove={move}
              />
            ) : (
              <BoxStep
                boxes={editableBoxes}
                query={query}
                currentBoxId={memo.boxId}
                onPick={pickBox}
              />
            )}
          </div>
        </DialogPanel>
      </div>
    </div>
  );
};

export default MoveMemoModal;

// MARK: - Step 1: boxes

const BoxStep: FC<{
  boxes: Box[];
  query: string;
  currentBoxId: string;
  onPick: (box: Box) => void;
}> = ({ boxes, query, currentBoxId, onPick }) => {
  const t = useTranslations();
  const { boxName, accessLevel } = useBoxLabels();
  const q = query.trim().toLowerCase();
  // The memo's own box first, the rest in the order they came.
  const filtered = boxes
    .filter((box) => boxName(box.name).toLowerCase().includes(q))
    .sort(
      (a, b) => Number(b.id === currentBoxId) - Number(a.id === currentBoxId),
    );

  if (filtered.length === 0) {
    return <p className={styles.empty}>{t('boxes.noneFound')}</p>;
  }

  return (
    <>
      {filtered.map((box) => {
        // Subtle hint of where the memo lives now — drilling into this box
        // then reveals the exact collection (also flagged there).
        const isCurrent = box.id === currentBoxId;

        return (
          <button
            key={box.id}
            type="button"
            className={styles.row}
            onClick={() => onPick(box)}
          >
            <BoxTile box={box} />
            <span className={styles.rowText}>
              <span className={styles.rowTitle}>{boxName(box.name)}</span>
              <span className={styles.rowSubtitle}>{accessLevel(box)}</span>
            </span>
            {isCurrent && (
              <span className={styles.currentHint}>
                {t('moveMemo.current')}
              </span>
            )}
            <FontAwesomeIcon
              icon={faChevronRight}
              className={styles.rowChevron}
            />
          </button>
        );
      })}
    </>
  );
};

const BoxTile: FC<{ box: Box }> = ({ box }) => {
  const color = box.display?.tile.color ?? null;
  const emoji = box.display?.tile.emoji ?? null;

  return (
    <span
      className={styles.boxTile}
      style={color ? { backgroundColor: color } : undefined}
      aria-hidden="true"
    >
      {emoji ? (
        <span className={styles.boxTileEmoji}>{emoji}</span>
      ) : (
        <BoxIcon
          className={styles.boxTileIcon}
          style={color ? { color: contrastingForeground(color) } : undefined}
        />
      )}
    </span>
  );
};

// MARK: - Step 2: collections of the chosen box

const CollectionStep: FC<{
  box: Box;
  memo: Memo;
  query: string;
  disabled: boolean;
  onMove: (box: Box, collection?: Collection) => void;
}> = ({ box, memo, query, disabled, onMove }) => {
  const t = useTranslations();
  const { data: collections = [], isLoading } = useQuery(
    collectionsQuery(box.id),
  );

  const q = query.trim().toLowerCase();
  const filtered = collections.filter((c) => c.name.toLowerCase().includes(q));
  const timelineIsCurrent = memo.boxId === box.id && !memo.collectionId;

  return (
    <>
      {/* "Timeline" = the box with no collection. Always available as a target
          and not filtered out by the search. */}
      <button
        type="button"
        className={styles.row}
        disabled={disabled || timelineIsCurrent}
        onClick={() => onMove(box)}
      >
        <span className={styles.timelineTile}>
          <FontAwesomeIcon icon={faClock} />
        </span>
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>
            {t('collections.views.timeline.title')}
          </span>
          <span className={styles.rowSubtitle}>
            {t('moveMemo.timelineSubtitle')}
          </span>
        </span>
        {timelineIsCurrent && (
          <span className={styles.current}>{t('moveMemo.current')}</span>
        )}
      </button>

      {isLoading ? (
        <p className={styles.empty}>{t('common.loading')}</p>
      ) : (
        filtered.map((collection) => {
          const isCurrent =
            memo.boxId === box.id && memo.collectionId === collection.id;

          return (
            <button
              key={collection.id}
              type="button"
              className={styles.row}
              disabled={disabled || isCurrent}
              onClick={() => onMove(box, collection)}
            >
              <span className={styles.collectionTile}>
                {collection.emoji ?? (
                  <CollectionIcon className={styles.collectionGlyph} />
                )}
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowTitle}>{collection.name}</span>
              </span>
              {isCurrent && (
                <span className={styles.current}>{t('moveMemo.current')}</span>
              )}
            </button>
          );
        })
      )}

      {!isLoading && q !== '' && filtered.length === 0 && (
        <p className={styles.empty}>{t('collections.noneFound')}</p>
      )}
    </>
  );
};

const styles = {
  outer: 'fixed inset-0 z-10 w-screen overflow-y-auto',
  inner: 'flex min-h-full items-center justify-center p-4',
  panel: classNames(
    'flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl bg-white text-left shadow-xl transition-all',
    'data-closed:opacity-0 data-closed:scale-95 data-enter:duration-200 data-leave:duration-150',
  ),
  header: {
    container: 'flex items-start justify-between gap-3 px-6 pt-6 pb-3',
    titleWrap: 'flex items-start gap-2 min-w-0',
    back: 'flex-none mt-0.5 text-gray-400 hover:text-gray-600',
    title: 'text-lg font-bold text-mf-off-black-950 truncate',
    subtitle: 'text-sm text-gray-500',
    close:
      'flex-none flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200',
  },
  search: {
    wrap: 'px-6 pb-3',
    field: 'relative',
    icon: 'pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400',
    input: classNames(
      'w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-slate-800',
      'placeholder:text-gray-400 focus:border-mf-blue focus:bg-white focus:outline-none',
    ),
  },
  list: 'flex-1 overflow-y-auto px-3 pb-4 space-y-0.5',
  row: classNames(
    'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors',
    'hover:bg-gray-100 focus:outline-none disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
  ),
  boxTile:
    'flex-none flex items-center justify-center w-10 h-10 rounded-[10px] bg-gray-100',
  boxTileEmoji: 'text-xl leading-none',
  boxTileIcon: 'w-5 h-5 text-gray-500',
  timelineTile:
    'flex-none flex items-center justify-center w-10 h-10 rounded-[10px] bg-gray-100 text-gray-500',
  collectionTile:
    'flex-none flex items-center justify-center w-10 h-10 text-xl leading-none',
  collectionGlyph: 'w-5 h-5 text-gray-400',
  rowText: 'flex-1 min-w-0 flex flex-col',
  rowTitle: 'text-sm font-medium text-slate-800 truncate',
  rowSubtitle: 'text-xs text-gray-500 truncate',
  rowChevron: 'flex-none text-sm text-gray-400',
  current: 'flex-none text-xs font-medium text-mf-blue',
  currentHint:
    'flex-none rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-400',
  empty: 'px-3 py-8 text-center text-sm text-gray-400',
};
